import { computed, effect, inject, Injector, runInInjectionContext, signal, Signal, untracked } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { parsePagedResponse } from './paged-response';
import { adminUrl } from './admin-http';
// `SortState` mora em `shared/tabela` (junto da tabela que emite o sort) — este
// arquivo só o CONSOME. Não reexporte daqui: o barrel do admin já faz
// `export * from 'src/app/shared/tabela'`, e dois `export *` com o mesmo nome
// se anulam em silêncio (o nome vira ambíguo e some da superfície pública).
import type { SortState } from 'src/app/shared/tabela/tipos';

/** Query params adicionais de uma lista admin (valores vazios são omitidos). */
export type AdminQueryParams = Record<string, string | number | boolean | null | undefined>;

/** Query resolvida de um fetch: `page`/`size` mais busca, sort e extras. */
export type PagedListQuery = Record<string, string>;

/** Como o resultado de um fetch entra na lista. Ver `fetch()`. */
type ModoFetch = 'substitui' | 'anexa' | 'recarrega';

export interface PagedListConfig<T> {
  /**
   * Constante de endpoint (ex.: ADMIN_USUARIOS_BUSCAR) já resolvida em string.
   * Dispensável quando a tela passa `fetcher`.
   */
  endpoint?: string;
  /**
   * Substitui o GET padrão por uma chamada do client v3 gerado. A URL e os
   * tipos passam a viver no client — fonte única com o contrato — em vez de
   * numa constante de endpoint daqui. Recebe a query já montada (page, size,
   * filtro, sort e os extras de `params`), com os vazios omitidos.
   */
  /**
   * Aceita Promise além de Observable: o cliente orval passou a gerar funções baseadas em Promise
   * com o client `angular-query`, e o PagedList é quem normaliza — em vez de cada tela envolver
   * com `from()`.
   *
   * O `signal` existe porque `unsubscribe()` numa Promise já iniciada só descarta a resposta: o
   * XHR segue até o fim no backend. As funções geradas aceitam `options.signal` — repasse-o para
   * o cancelamento continuar real. É opcional: fetcher que o ignora continua compilando, só perde
   * o aborto.
   */
  fetcher?: (query: PagedListQuery, signal?: AbortSignal) => Observable<unknown> | Promise<unknown>;
  /**
   * Placeholders `$param$` do endpoint (ex.: { idEvento }). Aceita um objeto
   * fixo ou uma função — use a forma-função quando o valor vem de um signal
   * `input()`: no momento em que `createPagedList` é chamado (field
   * initializer / construtor), inputs de signal ainda não foram resolvidos
   * pelo Angular, então ler `this.id()` direto captura sempre o valor inicial
   * (`''`). A forma-função é reavaliada dentro do `effect()` do fetch inicial,
   * que só dispara depois que os inputs já foram atribuídos.
   */
  pathParams?: Record<string, string | number> | (() => Record<string, string | number>);
  /**
   * Query params adicionais mesclados em toda requisição. Aceita um objeto
   * fixo ou uma função avaliada a cada fetch — use a forma-função para filtros
   * dinâmicos (leia signals dentro dela e chame `setPage(0)` ao trocar filtro).
   * Entradas `null`/`undefined`/`''` são omitidas.
   */
  params?: AdminQueryParams | (() => AdminQueryParams);
  /** Tamanho de página inicial (default 10). */
  initialPageSize?: number;
  /** Ordenação inicial. */
  initialSort?: SortState | null;
  /**
   * Busca e página iniciais. Existem para uma tela nascer já filtrada a partir
   * da URL (deeplink) sem pagar um fetch a mais: chamar `setSearch`/`setPage`
   * depois de criar a lista dispararia uma segunda requisição.
   */
  initialSearch?: string;
  initialPageIndex?: number;
  /** Transforma cada linha crua antes de expor (opcional). */
  mapRow?: (raw: unknown) => T;
}

/**
 * Controlador de lista server-side, próprio (HttpClient + signals). Substitui
 * `createByEntity` da dg. Semântica `staleTime: 0`: cada `refresh()` refaz o
 * fetch; como as telas de lista são rotas `loadComponent`, remontar já recarrega.
 *
 * Contrato de query params (paridade Flutter administrativo_datasource):
 *   - `filtro`  → busca textual (nome/email)
 *   - `page`    → índice 0-based
 *   - `size`    → tamanho de página
 *   - `sort`    → `campo,asc|desc` (omitido quando não há ordenação)
 *
 * DEVE ser chamado em contexto de injeção (field initializer / construtor).
 */
export interface PagedList<T> {
  rows: Signal<T[]>;
  loading: Signal<boolean>;
  error: Signal<unknown | null>;
  pageIndex: Signal<number>;
  pageSize: Signal<number>;
  totalRows: Signal<number>;
  totalPages: Signal<number>;
  search: Signal<string>;
  sort: Signal<SortState | null>;
  /** Existe página depois da atual (alimenta o scroll infinito). */
  hasMore: Signal<boolean>;
  /**
   * Carrega a próxima página e CONCATENA. É o único caminho que concatena —
   * busca, sort, troca de página e refresh continuam substituindo, senão o
   * usuário filtra e vê o resultado grudado no anterior.
   *
   * No-op na última página e enquanto uma requisição está em voo (o
   * IntersectionObserver do mobile dispara mais de uma vez por rolagem).
   */
  loadMore(): void;
  setSearch(term: string): void;
  setSort(sort: SortState | null): void;
  setPage(pageIndex: number): void;
  setPageSize(pageSize: number): void;
  refresh(): void;
}

/**
 * Preferência de "itens por página" do admin — persistida em localStorage
 * (mesmo padrão/prefixo das prefs da tabela: densidade/visibilidade). Global às
 * listas admin: definir 50 em uma tela vale para as demais.
 */
const PAGE_SIZE_STORAGE_KEY = 'admin-table:pageSize';

function readStoredPageSize(): number | null {
  try {
    const n = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function persistPageSize(n: number): void {
  try {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(n));
  } catch {
    /* localStorage indisponível */
  }
}

export function createPagedList<T = unknown>(config: PagedListConfig<T>): PagedList<T> {
  const http = inject(HttpClient);
  const injector = inject(Injector);

  const rows = signal<T[]>([]);
  const loading = signal<boolean>(false);
  const error = signal<unknown | null>(null);
  const pageIndex = signal<number>(Math.max(0, config.initialPageIndex ?? 0));
  // Prioriza a preferência salva do usuário sobre o default da tela.
  const pageSize = signal<number>(readStoredPageSize() ?? config.initialPageSize ?? 10);
  const totalRows = signal<number>(0);
  const totalPages = signal<number>(0);
  const search = signal<string>(config.initialSearch ?? '');
  const sort = signal<SortState | null>(config.initialSort ?? null);

  // Quantas páginas o `loadMore()` empilhou na lista atual. É o que o `refresh()` relê.
  // NÃO dá para deduzir de `pageIndex`: nas telas de paginador o índice avança sem
  // acumular, e ali um refresh que pedisse `pageIndex + 1` páginas despejaria a lista
  // inteira numa tela de 10 linhas.
  let paginasAcumuladas = 1;
  // Token de corrida: só o fetch mais recente pode escrever o resultado.
  let requestSeq = 0;
  // E a requisição anterior é CANCELADA de fato: só ignorar a resposta deixa o
  // backend terminando trabalho que ninguém vai ler — numa busca com debounce
  // curto isso vira uma fila de queries inúteis por usuário.
  //
  // Com fetcher baseado em Promise o unsubscribe não basta (a Promise já assinou por dentro), daí
  // o AbortController: é ele que aborta de verdade quando o fetcher repassa o signal.
  let emVoo: { unsubscribe(): void } | null = null;
  let abortoEmVoo: AbortController | null = null;

  function resolvePathParams(): Record<string, string | number> | undefined {
    return typeof config.pathParams === 'function' ? config.pathParams() : config.pathParams;
  }

  function buildQuery(modo: ModoFetch): PagedListQuery {
    // `recarrega` relê de uma vez TUDO que o `loadMore` já trouxe, numa requisição só.
    // Pedir a página atual isolada faria o operador que rolou até a página 3 perder as 75
    // linhas anteriores a cada bipada — e `checkin-page` chama `refresh()` em sete lugares,
    // todo check-in é um deles. Pedir página a página seria N+1 requisições no portão.
    const query: PagedListQuery =
      modo === 'recarrega' && paginasAcumuladas > 1
        ? { page: '0', size: String(paginasAcumuladas * pageSize()) }
        : { page: String(pageIndex()), size: String(pageSize()) };

    const term = search().trim();
    if (term) query['filtro'] = term;

    const s = sort();
    if (s) query['sort'] = `${s.id},${s.dir}`;

    const extra = typeof config.params === 'function' ? config.params() : config.params;
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        if (v === null || v === undefined || v === '') continue;
        query[k] = String(v);
      }
    }
    return query;
  }

  /**
   * `anexa` é exclusivo do `loadMore()`. `recarrega` é o `refresh()`, que relê o acumulado
   * inteiro. Todo o resto `substitui`, voltando para a página 0.
   */
  function fetch(modo: ModoFetch = 'substitui'): void {
    const seq = ++requestSeq;
    loading.set(true);
    error.set(null);
    const query = buildQuery(modo);
    emVoo?.unsubscribe();
    abortoEmVoo?.abort();
    const aborto = new AbortController();
    abortoEmVoo = aborto;
    runInInjectionContext(injector, () => {
      const bruto = config.fetcher ? config.fetcher(query, aborto.signal) : null;
      const requisicao = bruto
        ? (bruto instanceof Promise ? from(bruto) : bruto)
        : http.get(adminUrl(config.endpoint ?? '', resolvePathParams()), {
            params: new HttpParams({ fromObject: query }),
          });
      emVoo = requisicao.subscribe({
        next: (resp) => {
          if (seq !== requestSeq) return; // resposta obsoleta
          const parsed = parsePagedResponse<unknown>(resp);
          const mapped = config.mapRow
            ? parsed.content.map(config.mapRow)
            : (parsed.content as T[]);
          if (modo === 'anexa') rows.update((atuais) => [...atuais, ...mapped]);
          else rows.set(mapped);
          totalRows.set(parsed.totalElements);
          // Em `recarrega` o servidor conta as páginas sobre o `size` inflado da requisição.
          // O paginador e o `hasMore` raciocinam no tamanho real, então reconta a partir do
          // total de elementos — senão o scroll infinito para de achar que há próxima página.
          totalPages.set(
            modo === 'recarrega'
              ? Math.ceil(parsed.totalElements / pageSize())
              : parsed.totalPages,
          );
          loading.set(false);
        },
        error: (err) => {
          if (seq !== requestSeq) return;
          error.set(err);
          // Numa falha de `loadMore` — ou de `refresh`, que é o mesmo acumulado — o que já
          // está na tela continua válido: limpar mandaria o usuário de volta ao topo de uma
          // lista vazia. Só busca, sort e troca de página esvaziam.
          if (modo === 'substitui') rows.set([]);
          loading.set(false);
        },
      });
    });
  }

  // Fetch inicial adiado para um `effect()`: se `pathParams` for uma função que
  // lê um signal `input()` (ex.: idEvento), rodar `fetch()` direto aqui (fora
  // de effect) capturaria o valor padrão do input, ainda não resolvido pelo
  // Angular neste ponto do ciclo de vida. `untracked` evita que os demais
  // signals lidos dentro de `fetch()` (search/sort/page/params) virem
  // dependência do effect — mudanças neles já dependem de chamadas explícitas
  // (setPage, setSearch, etc.).
  effect(() => {
    resolvePathParams();
    untracked(() => fetch());
  });

  return {
    rows: rows.asReadonly(),
    loading: loading.asReadonly(),
    error: error.asReadonly(),
    pageIndex: pageIndex.asReadonly(),
    pageSize: pageSize.asReadonly(),
    totalRows: totalRows.asReadonly(),
    totalPages: totalPages.asReadonly(),
    search: search.asReadonly(),
    sort: sort.asReadonly(),
    hasMore: computed(() => pageIndex() + 1 < totalPages()),
    loadMore(): void {
      if (loading() || pageIndex() + 1 >= totalPages()) return;
      pageIndex.set(pageIndex() + 1);
      paginasAcumuladas += 1;
      fetch('anexa');
    },
    setSearch(term: string): void {
      search.set(term ?? '');
      pageIndex.set(0);
      paginasAcumuladas = 1;
      fetch();
    },
    setSort(next: SortState | null): void {
      sort.set(next);
      pageIndex.set(0);
      paginasAcumuladas = 1;
      fetch();
    },
    setPage(pi: number): void {
      pageIndex.set(Math.max(0, pi));
      paginasAcumuladas = 1;
      fetch();
    },
    setPageSize(ps: number): void {
      pageSize.set(ps);
      persistPageSize(ps);
      pageIndex.set(0);
      paginasAcumuladas = 1;
      fetch();
    },
    refresh(): void {
      fetch('recarrega');
    },
  };
}

/** Utilitário: `computed` que indica se há alguma linha selecionada. */
export function anySelected(sel: Signal<Set<string | number>>): Signal<boolean> {
  return computed(() => sel().size > 0);
}
